"use client";
import { useState, useEffect, useRef } from "react";
import { cn } from "@repo/utils/cn";
import { Dropdown, DropdownOption } from "./Dropdown";

import { ErrorIcon } from "@repo/ui-parkmobile/icons/ErrorIcon";

interface PhoneCountryOption {
  label: string;
  value: string;
  flag?: string;
  numberLength?: number;
  validationMessage?: string;
}

export interface PhoneNumberSelectProps {
  id?: string;
  name?: string;
  placeholder?: string;
  value?: string;
  options?: PhoneCountryOption[];
  state?: "default" | "error";
  background?: "default" | "white";
  errorMessage?: string;
  onChange?: (countryCode: string, phoneNumber: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
}

export function PhoneNumberSelect({
  id,
  name,
  placeholder = "Placeholder",
  value = "",
  options = [],
  state = "default",
  background = "default",
  errorMessage,
  onChange,
  onFocus,
  onBlur,
  className,
}: PhoneNumberSelectProps) {
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (options.length > 0 && !selectedCountryCode) {
      const defaultOption =
        options.find((opt) => opt.value === "+46") || options[0];
      setSelectedCountryCode(defaultOption?.value || "");
    }
  }, [options, selectedCountryCode]);

  useEffect(() => {
    if (value && options.length > 0) {
      const matchingOption = options.find((option) =>
        value.startsWith(option.value),
      );

      if (matchingOption) {
        setSelectedCountryCode(matchingOption.value);
        setPhoneNumber(value.substring(matchingOption.value.length));
      } else {
        setPhoneNumber(value);
      }
    } else if (!value) {
      setPhoneNumber("");
    }
  }, [value, options]);

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountryCode(countryCode);
    onChange?.(countryCode, phoneNumber);
    inputRef.current?.focus();
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const number = e.target.value;
    setPhoneNumber(number);
    onChange?.(selectedCountryCode, number);
  };

  const handlePhoneBlur = () => {
    onBlur?.();
  };

  const selectedOption = options.find(
    (opt) => opt.value === selectedCountryCode,
  );

  // Convert PhoneCountryOption[] to DropdownOption[]
  const dropdownOptions: DropdownOption[] = options.map((option) => ({
    label: option.label,
    value: option.value,
    icon: option.flag ? (
      <img
        src={option.flag}
        alt={`${option.label} flag`}
        className="uipm:w-6 uipm:h-6 uipm:rounded-full uipm:object-cover"
      />
    ) : (
      <div className="uipm:w-6 uipm:h-6 uipm:rounded-full uipm:bg-gray-300" />
    ),
  }));

  const renderOption = (option: DropdownOption, isSelected: boolean) => (
    <div className="uipm:flex uipm:items-center uipm:gap-3 uipm:w-full">
      <div className="uipm:shrink-0">{option.icon}</div>
      <div className="uipm:flex uipm:items-center uipm:justify-between uipm:flex-1 uipm:gap-2">
        <span className="uipm:text-base uipm:truncate uipm:flex-1">
          {option.label.split(" || ")[0] || option.label}
        </span>
        <span className="uipm:text-[var(--color-color-accent)] uipm:text-base uipm:shrink-0">
          {option.value}
        </span>
      </div>
    </div>
  );

  const trigger = (
    <div
      className={cn(
        "uipm:relative uipm:flex uipm:items-center uipm:gap-2 uipm:px-4 uipm:py-3 uipm:min-h-[48px] uipm:rounded-xl uipm:cursor-pointer uipm:transition-colors",
        background === "white"
          ? "uipm:bg-white uipm:hover:bg-[var(--surface-surface-secondary)]"
          : "uipm:form-hover",
        "uipm:border-2 uipm:border-transparent",
        "focus-within:uipm:bg-[var(--surface-surface-primary)] focus-within:uipm:border-[var(--color-color-accent)]",
        state === "error" && "uipm:border-[var(--color-color-danger)]",
      )}
    >
      {/* Country Code Selector */}
      <div className="uipm:flex uipm:items-center uipm:gap-2 uipm:p-1 uipm:rounded-lg uipm:shrink-0">
        {selectedOption?.flag ? (
          <img
            src={selectedOption.flag}
            alt={`${selectedOption.label} flag`}
            className="uipm:w-6 uipm:h-6 uipm:rounded-full uipm:object-cover"
          />
        ) : (
          <div className="uipm:w-6 uipm:h-6 uipm:rounded-full uipm:bg-gray-300" />
        )}
        <span className="uipm:text-[var(--color-color-accent)] uipm:font-normal uipm:text-base uipm:leading-6 uipm:whitespace-nowrap">
          {selectedCountryCode}
        </span>
      </div>

      {/* Separator */}
      <div className="uipm:h-6 uipm:w-px uipm:bg-[var(--color-color-light)] uipm:shrink-0" />

      {/* Phone Number Input Container */}
      <div className="uipm:flex-1 uipm:flex uipm:items-center">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="tel"
          value={phoneNumber}
          placeholder={placeholder}
          onFocus={onFocus}
          onChange={handlePhoneChange}
          onBlur={handlePhoneBlur}
          className={cn(
            "uipm:w-full uipm:bg-transparent uipm:border-none uipm:outline-none uipm:text-base uipm:text-[var(--typography-colors-text-body)] placeholder:uipm:text-[var(--typography-colors-text-faded)] uipm:leading-6 uipm:font-normal",
            state === "error" && "uipm:pr-8",
          )}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Error Icon */}
      {state === "error" && (
        <div className="uipm:absolute uipm:right-4 uipm:top-1/2 uipm:-translate-y-1/2">
          <ErrorIcon />
        </div>
      )}
    </div>
  );

  return (
    <div className={cn("uipm:relative", className)}>
      <Dropdown
        trigger={trigger}
        options={dropdownOptions}
        value={selectedCountryCode}
        onValueChange={handleCountryChange}
        state={state}
        renderOption={renderOption}
        contentClassName="uipm:min-w-[250px]"
        showNoneOption={false}
      />
    </div>
  );
}
